(() => {
  if (customElements.get('tpu-cart-drawer')) return;

  const FREE_SHIPPING_THRESHOLD = 50000;
  const AUTO_CLOSE_MS = 3000;
  const AUTO_CLOSE_ENABLED = true;
  const CONFETTI_COLORS = ['#0559c9', '#16a34a', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4'];

  class TpuCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this._cartWrap = this.closest('[data-tpu-cart]');
      this._addedView = this.querySelector('[data-tpu-cart-view="added"]');
      this._cartView = this.querySelector('[data-tpu-cart-view="cart"]');
      this._emptyView = this.querySelector('.tpu-cart-drawer__empty');
      this._lineItemList = this.querySelector('[data-tpu-cart-items]');
      this._addedShipping = this.querySelector('[data-tpu-cart-added-shipping]');
      this._cartShipping = this.querySelector('[data-tpu-cart-cart-shipping]');
      this._addedSubtitle = this.querySelector('[data-tpu-cart-added-subtitle]');
      this._addedProduct = this.querySelector('[data-tpu-cart-added-product]');
      this._addedImage = this.querySelector('[data-tpu-cart-added-image]');
      this._addedTitle = this.querySelector('[data-tpu-cart-added-title]');
      this._cartTitle = this.querySelector('#TpuCartDrawerTitle');
      this._autoCloseNote = this.querySelector('[data-tpu-cart-auto-close]');
      this._addedPaypal = this.querySelector('[data-tpu-paypal-drawer="added"]');
      this._totalEls = this.querySelectorAll('.js-cart-total');
      this._toggle = this._cartWrap?.querySelector('[data-tpu-cart-toggle]');
      this._autoCloseTimer = null;
      this._confettiFrame = null;
      this._pendingQtyErrors = new Map();

      this._i18n = {
        addedOne: this.dataset.i18nAddedOne || '[count] item has been added to your cart.',
        addedOther: this.dataset.i18nAddedOther || '[count] items have been added to your cart.',
        cartTitle: this.dataset.i18nCartTitle || 'My Cart ([count])',
        removing: this.dataset.i18nRemoving || 'Removing',
        qtyMaxOne: this.dataset.i18nQtyMaxOne || 'Only [count] item available in stock.',
        qtyMaxOther: this.dataset.i18nQtyMaxOther || 'Only [count] items available in stock.',
        qtyErrorGeneric: this.dataset.i18nQtyErrorGeneric || 'This quantity is not available.'
      };

      this._bindLineItems();

      this.addEventListener('click', (event) => {
        const closeTarget = event.target.closest('[data-tpu-cart-close]');
        if (closeTarget) {
          event.preventDefault();
          this.close();
        }
      });

      if (this._toggle) {
        this._toggle.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this._clearAutoClose();
          if (this.isOpen && this._activeView === 'cart') {
            this.close();
          } else {
            this.open('cart');
          }
        });
      }

      document.addEventListener('click', (event) => {
        if (!this.isOpen || !this._cartWrap) return;
        if (this._cartWrap.contains(event.target)) return;
        this.close();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close();
      });

      document.addEventListener('cart-add', (event) => {
        // Show the "added" toast for every successful add (PDP, sticky ATC,
        // quick-add). common.js skips the legacy #cart-message popup whenever
        // <tpu-cart-drawer> exists, so this is the only confirmation path.
        this._handleCartAdd(event.detail || {});
      });

      document.addEventListener('cart-change', (event) => {
        const detail = event.detail || {};
        this._syncHeader(detail);
        if (typeof detail.item_count === 'number') {
          this._setItemCount(detail.item_count);
          this._setTotals(detail.total_price);
          if (detail.item_count === 0 && this.isOpen) {
            this._showEmptyView();
            return;
          }
        }
        if (this.isOpen && this._activeView === 'cart') {
          this._fetchAndRefresh();
        }
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(view = 'cart') {
      this._activeView = view;
      document.body.classList.add('cart-active');

      if (view === 'added') {
        this._showAddedView();
        if (this._activeView === 'added') this._watchAddedPaypalOutput();
        this._startAutoClose();
        this.classList.add('is-open');
        this._scheduleConfetti();
        return;
      }

      this._clearAutoClose();
      this._showCartView();
      this._fetchAndRefresh();
      this.classList.add('is-open');
    }

    close() {
      this._clearAutoClose();
      this._stopAddedPaypalWatch();
      this._stopConfetti();
      this.classList.remove('is-open');
      document.body.classList.remove('cart-active');
      delete this.dataset.activeView;
    }

    _activateView(view) {
      this.dataset.activeView = view;
      if (this._addedView) this._addedView.hidden = view !== 'added';
      if (this._cartView) this._cartView.hidden = view !== 'cart';
      if (this._emptyView) this._emptyView.hidden = view !== 'empty';
    }

    _showAddedView() {
      const count = parseInt(this.dataset.cartItemCount || '0', 10);
      if (count === 0) {
        this._showEmptyView();
        return;
      }

      this._activateView('added');
      const paypalEligible = Boolean(this._addedPaypal?.querySelector('[data-pp-message]'));
      if (this._autoCloseNote) {
        this._autoCloseNote.hidden = !AUTO_CLOSE_ENABLED || paypalEligible;
      }
      if (paypalEligible) this._clearAutoClose();
    }

    _showCartView() {
      const count = parseInt(this.dataset.cartItemCount || '0', 10);
      if (count === 0) {
        this._showEmptyView();
        return;
      }

      this._activateView('cart');
    }

    _showEmptyView() {
      this._activateView('empty');
      this._stopAddedPaypalWatch();
      this.querySelectorAll('[data-tpu-paypal-drawer]').forEach((wrapper) => wrapper.replaceChildren());
      this._setItemCount(0);

      const emptyTitle = this._emptyView?.querySelector('.tpu-cart-drawer__title');
      if (emptyTitle) emptyTitle.textContent = this._formatCartTitle(0);
    }

    _startAutoClose() {
      if (!AUTO_CLOSE_ENABLED || this._hasAddedPaypalMount()) return;

      this._clearAutoClose();
      this._autoCloseTimer = window.setTimeout(() => this.close(), AUTO_CLOSE_MS);
    }

    _hasAddedPaypalMount() {
      return Boolean(this._addedPaypal?.querySelector('[data-pp-message]'));
    }

    _watchAddedPaypalOutput() {
      if (!this._addedPaypal || this._addedPaypalObserver) return;
      const handle = () => {
        if (!this.isOpen || this._activeView !== 'added') return;
        if (this._hasAddedPaypalMount()) {
          this._clearAutoClose();
          if (this._autoCloseNote) this._autoCloseNote.hidden = true;
        }
      };
      if (typeof MutationObserver !== 'function') return;
      this._addedPaypalObserver = new MutationObserver(handle);
      this._addedPaypalObserver.observe(this._addedPaypal, { childList: true, subtree: true });
      handle();
    }

    _stopAddedPaypalWatch() {
      this._addedPaypalObserver?.disconnect();
      this._addedPaypalObserver = null;
    }

    _clearAutoClose() {
      if (this._autoCloseTimer) {
        window.clearTimeout(this._autoCloseTimer);
        this._autoCloseTimer = null;
      }
    }

    _scheduleConfetti(attempt = 0) {
      if (this._activeView !== 'added' || !this.isOpen) return;

      requestAnimationFrame(() => {
        const played = this._playConfetti();
        if (!played && attempt < 8) {
          window.setTimeout(() => this._scheduleConfetti(attempt + 1), 50);
        }
      });
    }

    _stopConfetti() {
      if (this._confettiFrame) {
        cancelAnimationFrame(this._confettiFrame);
        this._confettiFrame = null;
      }

      const canvas = this._addedView?.querySelector('[data-tpu-cart-confetti]');
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    _playConfetti() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;

      const host = this._addedView;
      const originEl = host?.querySelector('.tpu-cart-drawer__added-check');
      if (!host || !originEl || host.hidden || !this.isOpen) return false;

      const hostRect = host.getBoundingClientRect();
      if (hostRect.width < 1 || hostRect.height < 1) return false;

      let canvas = host.querySelector('[data-tpu-cart-confetti]');
      if (!canvas) {
        canvas = document.createElement('canvas');
        canvas.className = 'tpu-cart-drawer__confetti';
        canvas.dataset.tpuCartConfetti = '';
        canvas.setAttribute('aria-hidden', 'true');
        host.appendChild(canvas);
      }

      const originRect = originEl.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const width = hostRect.width;
      const height = hostRect.height;

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) return false;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const originX = originRect.left + originRect.width / 2 - hostRect.left;
      const originY = originRect.top + originRect.height / 2 - hostRect.top;

      const particles = Array.from({ length: 64 }, () => ({
        x: originX,
        y: originY,
        vx: (Math.random() - 0.5) * 10,
        vy: Math.random() * -10 - 4,
        w: Math.random() * 7 + 4,
        h: Math.random() * 5 + 3,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 16,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        opacity: 1,
        decay: Math.random() * 0.012 + 0.009,
        gravity: 0.22
      }));

      if (this._confettiFrame) {
        cancelAnimationFrame(this._confettiFrame);
        this._confettiFrame = null;
      }

      const start = performance.now();
      const duration = 2200;

      const animate = (now) => {
        ctx.clearRect(0, 0, width, height);

        let alive = false;

        particles.forEach((particle) => {
          particle.vy += particle.gravity;
          particle.x += particle.vx;
          particle.y += particle.vy;
          particle.vx *= 0.985;
          particle.rotation += particle.rotationSpeed;
          particle.opacity -= particle.decay;

          if (particle.opacity <= 0) return;

          alive = true;
          ctx.save();
          ctx.translate(particle.x, particle.y);
          ctx.rotate((particle.rotation * Math.PI) / 180);
          ctx.globalAlpha = Math.max(particle.opacity, 0);
          ctx.fillStyle = particle.color;
          ctx.fillRect(-particle.w / 2, -particle.h / 2, particle.w, particle.h);
          ctx.restore();
        });

        if (alive && now - start < duration) {
          this._confettiFrame = requestAnimationFrame(animate);
        } else {
          ctx.clearRect(0, 0, width, height);
          this._confettiFrame = null;
        }
      };

      this._confettiFrame = requestAnimationFrame(animate);
      return true;
    }

    _formatMoney(cents) {
      if (typeof cents !== 'number') return '';
      if (typeof Number.prototype.toCurrency === 'function') return cents.toCurrency();
      return (cents / 100).toFixed(2);
    }

    _formatAddedSubtitle(count) {
      const template = count === 1 ? this._i18n.addedOne : this._i18n.addedOther;
      return template.replace(/\[count\]/g, String(count));
    }

    _formatCartTitle(count) {
      const safeCount = Number.isFinite(count) ? count : 0;
      return this._i18n.cartTitle.replace(/\[count\]/g, String(safeCount));
    }

    _formatQtyMaxError(max) {
      const template = max === 1 ? this._i18n.qtyMaxOne : this._i18n.qtyMaxOther;
      return template.replace(/\[count\]/g, String(max));
    }

    _showQtyError(lineItem, message) {
      const lineId = lineItem?.dataset?.key;
      if (lineId) this._pendingQtyErrors.set(lineId, message);

      const errorEl = lineItem?.querySelector('[data-tpu-cart-qty-error]');
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    _clearQtyError(lineItem) {
      const lineId = lineItem?.dataset?.key;
      if (lineId) this._pendingQtyErrors.delete(lineId);

      const errorEl = lineItem?.querySelector('[data-tpu-cart-qty-error]');
      if (errorEl) errorEl.hidden = true;
    }

    _reapplyPendingQtyErrors() {
      if (!this._pendingQtyErrors.size || !this._lineItemList) return;

      this._pendingQtyErrors.forEach((message, lineId) => {
        const lineItem = [...this._lineItemList.querySelectorAll('.js-cart-line-item')].find(
          (el) => el.dataset.key === lineId
        );
        if (!lineItem) return;

        const errorEl = lineItem.querySelector('[data-tpu-cart-qty-error]');
        if (!errorEl) return;

        errorEl.textContent = message;
        errorEl.hidden = false;
      });
    }

    _syncQtyChange(lineItem, qtyInput, lineId, requestedQty) {
      const input = qtyInput.querySelector("input[name='quantity']");
      const previousQty = input ? input.value : requestedQty;
      const max = input?.max ? parseInt(input.max, 10) : null;
      let qtyToSet = requestedQty;
      let showMaxError = false;

      if (max && requestedQty > max) {
        qtyToSet = max;
        showMaxError = true;
        if (input) input.value = String(max);
      }

      return window.Cart.change(lineId, qtyToSet)
        .then((res) => {
          if (!res?.items) throw res;

          const item = res.items.find((cartItem) => cartItem.key === lineId);
          const priceEl = lineItem.querySelector('.js-line-item-price');

          if (item) {
            if (priceEl && typeof item.final_line_price === 'number') {
              priceEl.innerHTML = item.final_line_price.toCurrency();
            }
            if (input) input.value = item.quantity;

            if (showMaxError || requestedQty > item.quantity) {
              const cap = max || item.quantity;
              this._showQtyError(lineItem, this._formatQtyMaxError(cap));
            } else {
              this._clearQtyError(lineItem);
            }
          }

          document.dispatchEvent(new CustomEvent('cart-change', { detail: res }));
        })
        .catch((err) => {
          const message = err?.description || err?.message || this._i18n.qtyErrorGeneric;
          this._showQtyError(lineItem, message);

          return window.Cart.get().then((cart) => {
            const item = cart.items.find((cartItem) => cartItem.key === lineId);
            if (item && input) {
              input.value = item.quantity;
            } else if (input) {
              input.value = previousQty;
            }
          });
        });
    }

    _syncHeader(cart) {
      if (!cart) return;
      document.querySelectorAll('.js-cart-item-count').forEach((el) => {
        el.textContent = cart.item_count;
      });
      document.querySelectorAll('.js-cart-item-total').forEach((el) => {
        el.innerHTML = this._formatMoney(cart.total_price);
      });
    }

    _setItemCount(count) {
      this.dataset.cartItemCount = String(count);
      if (this._cartTitle) this._cartTitle.textContent = this._formatCartTitle(count);
    }

    _setTotals(cents) {
      this._totalEls.forEach((el) => {
        el.innerHTML = this._formatMoney(cents);
      });
      this.querySelectorAll('[data-tpu-paypal-drawer]').forEach((wrapper) => {
        if (!Number.isFinite(cents) || cents <= 0) {
          wrapper.replaceChildren();
          return;
        }
        const message = document.createElement('div');
        message.setAttribute('data-pp-message', '');
        message.setAttribute('data-pp-placement', 'cart');
        message.setAttribute('data-pp-amount', (cents / 100).toFixed(2));
        message.setAttribute('data-pp-style-layout', 'text');
        message.setAttribute('data-pp-style-logo-type', 'inline');
        message.setAttribute('data-pp-style-text-color', 'black');
        message.setAttribute('data-pp-style-text-size', window.innerWidth >= 768 ? '13' : '15');
        wrapper.replaceChildren(message);
      });
      this._syncEarnCredit(cents);
    }

    _syncEarnCredit(cents) {
      const count = parseInt(this.dataset.cartItemCount || '0', 10);
      const cap = parseInt(this.dataset.tpuEarnCap || '5000', 10);
      this.querySelectorAll('[data-tpu-earn-credit]').forEach((el) => {
        if (!Number.isFinite(cents) || count === 0) {
          el.hidden = true;
          return;
        }
        const earn = Math.min(Math.floor(cents / 100), cap);
        el.hidden = false;
        el.setAttribute('data-tpu-earn-amount', String(earn));
        const textEl = el.querySelector('[data-tpu-earn-text]');
        if (textEl) {
          const template = el.dataset.tpuEarnTemplate || '';
          const formatted = typeof earn.toCurrency === 'function' ? earn.toCurrency() : (earn / 100).toFixed(2);
          textEl.textContent = template.replace('[amount]', formatted);
        }
      });
    }

    _bindLineItems() {
      if (!this._lineItemList) return;

      this._lineItemList.querySelectorAll('quantity-input').forEach((qtyInput) => {
        if (qtyInput.dataset.bound) return;
        qtyInput.dataset.bound = 'true';

        const lineItem = qtyInput.closest('.js-cart-line-item');
        const lineId = lineItem?.dataset.key;
        if (!lineId) return;

        const bindChange = () => {
          qtyInput.addEventListener('click', (event) => {
            if (!event.target.closest('quantity-input-plus')) return;

            const input = qtyInput.querySelector("input[name='quantity']");
            if (!input?.max) return;

            const max = parseInt(input.max, 10);
            const current = parseInt(input.value, 10) || 0;
            if (current >= max) {
              event.preventDefault();
              event.stopImmediatePropagation();
              this._showQtyError(lineItem, this._formatQtyMaxError(max));
            }
          }, true);

          qtyInput.onChange((quantity) => {
            if (!window.Cart) return;

            const qty = parseInt(quantity, 10);
            if (!qty || qty < 1) return;

            this._syncQtyChange(lineItem, qtyInput, lineId, qty);
          });
        };

        if (customElements.get('quantity-input')) {
          bindChange();
        } else {
          customElements.whenDefined('quantity-input').then(bindChange);
        }
      });

      this._lineItemList.querySelectorAll('.js-btn-remove').forEach((button) => {
        if (button.dataset.bound) return;
        button.dataset.bound = 'true';
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const lineId = button.dataset.lineId;
          if (!lineId || !window.Cart) return;
          button.disabled = true;
          const label = button.querySelector('span');
          if (label) label.textContent = this._i18n.removing;
          window.Cart.remove(lineId).then(() => {
            this._pendingQtyErrors.delete(lineId);
            button.closest('.js-cart-line-item')?.remove();
          });
        });
      });
    }

    _refreshFromHtml(html) {
      if (!html) return;
      const parsed = document.createElement('div');
      parsed.innerHTML = html;

      const jsonEl = parsed.querySelector('[data-cart-json]');
      let cart = null;
      if (jsonEl) {
        try {
          cart = JSON.parse(jsonEl.textContent);
        } catch (error) {
          cart = null;
        }
      }

      const fragments = parsed.querySelector('[data-ajax-cart-fragments]');
      if (fragments) {
        const lineItems = fragments.querySelector('[data-fragment="line-items"]');
        const shippingInline = fragments.querySelector('[data-fragment="shipping-inline"]');
        const shippingBanner = fragments.querySelector('[data-fragment="shipping-banner"]');

        if (lineItems && this._lineItemList) {
          this._lineItemList.innerHTML = lineItems.innerHTML;
          this._bindLineItems();
          this._reapplyPendingQtyErrors();
        }
        if (shippingInline && this._addedShipping) {
          this._addedShipping.innerHTML = shippingInline.innerHTML;
        }
        if (shippingBanner && this._cartShipping) {
          this._cartShipping.innerHTML = shippingBanner.innerHTML;
        }
      } else if (this._lineItemList) {
        this._lineItemList.innerHTML = '';
        parsed.querySelectorAll('.js-cart-line-item').forEach((item) => {
          this._lineItemList.appendChild(item);
        });
        this._bindLineItems();
        this._reapplyPendingQtyErrors();
      }

      if (cart) {
        this._setItemCount(cart.item_count);
        this._setTotals(cart.total_price);
        this._syncHeader(cart);

        if (cart.item_count === 0 && this._activeView === 'cart') {
          this._showEmptyView();
        }
      }
    }

    _fetchAndRefresh() {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return fetch(`${root}?sections=ajax-cart`)
        .then((response) => response.json())
        .then((data) => this._refreshFromHtml(data['ajax-cart']))
        .catch(() => {});
    }

    _handleCartAdd(detail) {
      const sections = detail.sections;
      const showAdded = () => {
        const qty = detail.quantity || 1;
        if (this._addedSubtitle) {
          this._addedSubtitle.textContent = this._formatAddedSubtitle(qty);
        }

        const imageUrl = detail.image || (detail.featured_image && detail.featured_image.url) || '';
        const title = detail.product_title || detail.title || '';

        if (this._addedProduct && title) {
          this._addedProduct.hidden = false;
          if (this._addedTitle) this._addedTitle.textContent = title;
          if (this._addedImage) {
            this._addedImage.innerHTML = imageUrl
              ? `<img src="${imageUrl}" alt="" width="100" height="100" loading="lazy">`
              : '';
          }
        } else if (this._addedProduct) {
          this._addedProduct.hidden = true;
        }

        const lineKey = String(detail.key || '');
        const addedLine = lineKey && this._lineItemList
          ? this._lineItemList.querySelector(`[data-key="${lineKey}"]`)
          : null;
        if (addedLine && addedLine.dataset.bulk === 'true' && this._addedShipping) {
          this._addedShipping.innerHTML = '';
        }

        this.open('added');
      };

      if (sections && sections['ajax-cart']) {
        this._refreshFromHtml(sections['ajax-cart']);
        showAdded();
        return;
      }

      this._fetchAndRefresh().then(showAdded);
    }
  }

  customElements.define('tpu-cart-drawer', TpuCartDrawer);

  const clearEarnInfoPosition = (panel) => {
    if (!panel) return;
    panel.classList.remove('is-fixed');
    panel.style.removeProperty('top');
    panel.style.removeProperty('left');
    panel.style.removeProperty('right');
    panel.style.removeProperty('bottom');
    panel.style.removeProperty('width');
    panel.style.removeProperty('max-width');
  };

  const placeEarnInfoPanel = (details) => {
    const panel = details.querySelector('.tpu-earn-credit__details');
    const btn = details.querySelector('.tpu-earn-credit__info-btn');
    if (!panel || !btn) return;

    const rect = btn.getBoundingClientRect();
    const gap = 6;
    const maxWidth = Math.min(264, window.innerWidth - 16);
    panel.classList.add('is-fixed');
    panel.style.width = `${maxWidth}px`;
    panel.style.maxWidth = `${maxWidth}px`;
    panel.style.right = 'auto';
    panel.style.bottom = 'auto';

    const panelHeight = panel.getBoundingClientRect().height || 72;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const placeAbove = spaceBelow < panelHeight + 8 && rect.top > panelHeight + gap;

    let top = placeAbove ? rect.top - panelHeight - gap : rect.bottom + gap;
    top = Math.max(8, Math.min(top, window.innerHeight - panelHeight - 8));

    let left = rect.right - maxWidth;
    left = Math.max(8, Math.min(left, window.innerWidth - maxWidth - 8));

    panel.style.top = `${top}px`;
    panel.style.left = `${left}px`;
  };

  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.classList.contains('tpu-earn-credit__info')) return;

    const panel = details.querySelector('.tpu-earn-credit__details');
    if (!details.open) {
      clearEarnInfoPosition(panel);
      return;
    }

    document.querySelectorAll('.tpu-earn-credit__info[open]').forEach((el) => {
      if (el !== details) el.removeAttribute('open');
    });

    placeEarnInfoPanel(details);
  }, true);

  const repositionOpenEarnInfo = () => {
    document.querySelectorAll('.tpu-earn-credit__info[open]').forEach((details) => {
      placeEarnInfoPanel(details);
    });
  };

  window.addEventListener('resize', repositionOpenEarnInfo);
  document.addEventListener('scroll', repositionOpenEarnInfo, true);

  document.addEventListener('click', (event) => {
    document.querySelectorAll('.tpu-earn-credit__info[open]').forEach((el) => {
      if (!el.contains(event.target)) {
        el.removeAttribute('open');
        clearEarnInfoPosition(el.querySelector('.tpu-earn-credit__details'));
      }
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.tpu-earn-credit__info[open]').forEach((el) => {
      el.removeAttribute('open');
      clearEarnInfoPosition(el.querySelector('.tpu-earn-credit__details'));
    });
  });
})();
